import { useCallback } from 'react';

import { InlineAlert } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { FlatList, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CardModalHeader } from 'components/card-modal-header';
import {
  CenteredRowContainer,
  centeredRowContainerStyles,
} from 'components/layout/centered-row-container';
import { useModalAnalyticsAppearanceEvent } from 'hooks/use-modal-analytics-appearance-event';
import { t } from 'state/intl/translations';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyOfferGroupsById } from 'state/loyalty/hooks/use-loyalty-offer-groups-by-id';
import { AppSystemwideOffer, SanityId } from 'state/loyalty/types';
import { WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { routes } from 'utils/routing';

import { LoadingAnimation } from '../loyalty-claim-points/loyalty-claim-points.styled';

import { renderOfferItem } from './loyalty-list-item';

export const OffersGroupsDetails: React.FC = () => {
  const { offerGroupId } = useLocalSearchParams<{ offerGroupId?: SanityId }>();

  const { setAddOffersCallbackUrl } = useLoyaltyContext();
  const { offers, groupName, canOnlyChooseOne, loading: offersLoading } = useLoyaltyOfferGroupsById(
    offerGroupId
  );
  const router = useRouter();

  const handleOfferPress = useCallback(
    (incentive: AppSystemwideOffer) => {
      const incentiveId = incentive.loyaltyEngineId;
      const qs = offerGroupId ? `?groupId=${offerGroupId}` : '';
      router.navigate(`${routes.loyaltyOfferList}/${incentiveId}${qs}`);
    },
    [router, offerGroupId]
  );

  const renderItem = ({ item }: { item: AppSystemwideOffer }) => {
    return renderOfferItem({
      item,
      handlePress: () => {
        if (item?.inlineAlertMessage) {
          return;
        }
        setAddOffersCallbackUrl(`${routes.loyaltyOfferGroup}/${offerGroupId}`);
        handleOfferPress(item);
      },
      skipAnimation: true,
    });
  };
  useModalAnalyticsAppearanceEvent('Offers Group Details');

  const { top } = useSafeAreaInsets();
  const headerText = groupName || t('selectOffer');

  return (
    <View testID="offers-group-items" style={{ flex: 1, paddingTop: top - 16 }}>
      <CenteredRowContainer standardPadding>
        <CardModalHeader
          {...{
            headerText,
            dismissType: 'back',
            containerStyle: { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
          }}
        />
      </CenteredRowContainer>
      {canOnlyChooseOne && (
        <InlineAlert
          status="info"
          marginBottom={'$4'}
          message={
            <>
              {t('canOnlyChooseOneOfferLine1')}
              {'\n'}
              {t('canOnlyChooseOneOfferLine2')}
            </>
          }
        />
      )}
      {offersLoading ? (
        <LoadingAnimation />
      ) : (
        <FlatList
          data={offers}
          contentContainerStyle={styles.flatList}
          keyExtractor={(item, index) => item._id || `${index}`}
          renderItem={renderItem}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  flatList: {
    ...centeredRowContainerStyles.content,
    maxWidth: WEB_SMALL_MAX_WIDTH,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$4,
  },
});
